import { CommonModule, isPlatformBrowser } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import {
  Component,
  DestroyRef,
  EventEmitter,
  OnInit,
  PLATFORM_ID,
  inject,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, ReactiveFormsModule } from '@angular/forms';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { MatSnackBarModule } from '@angular/material/snack-bar';
import { Router } from '@angular/router';

import { TranslateModule } from '@ngx-translate/core';
import { BehaviorSubject, finalize, first, switchMap } from 'rxjs';

import { AuthService } from '@personal-area/global/authorization/services';
import {
  AlertModule,
  FormFieldComponent,
  CountdownTimerModule,
  CountdownTimer,
  ButtonComponent,
  CardWrapperModule,
  ProgressModule,
} from '@personal-area/global/ui';
import { CryptoStorageService } from '@personal-area/storage';
import { I18nService } from '@personal-area/global/i18n';
import { UserFacade, UserSocketService } from '@personal-area/global/users/services';
import { ApiFormService, Validators } from '@personal-area/global/form-validation/services';
import { EmailConfirmationApiService } from '@personal-area/global/email-confirmation';

import {
  SkipData,
  SkipVerificationDialogComponent,
} from '../../../components/skip-verification-dialog/skip-verification-dialog.component';
import { BasicVerificationStatus } from '../../../models/verification-profile-response';
import { SummaryVerificationFacade } from '../../../state/facades/summary';

export const errorTimeKey = 'email-retry-error-key';
const defaultTimeout = 60000;

@Component({
  selector: 'global-email-basic',
  standalone: true,
  imports: [
    CommonModule,
    ButtonComponent,
    CardWrapperModule,
    FormFieldComponent,
    ReactiveFormsModule,
    TranslateModule,
    MatSnackBarModule,
    CountdownTimerModule,
    AlertModule,
    ProgressModule,
  ],
  templateUrl: './email-basic.component.html',
  styleUrls: ['./email-basic.component.scss'],
})
export class EmailBasicComponent implements OnInit {
  private readonly _formBuilder = inject(FormBuilder);
  private readonly _dialog = inject(MatDialog);
  private readonly _i18nService = inject(I18nService);
  private readonly _userFacade = inject(UserFacade);
  private readonly _authService = inject(AuthService);
  private readonly _formService = inject(ApiFormService);
  private readonly _cryptoStorageService = inject(CryptoStorageService);
  private readonly _platformId = inject(PLATFORM_ID);
  private readonly _userSocket = inject(UserSocketService);
  private readonly _router = inject(Router);
  private readonly _summaryFacade = inject(SummaryVerificationFacade);
  private readonly _emailConfirmationApiService = inject(EmailConfirmationApiService);

  private destroyRef = inject(DestroyRef);

  public form = this._formBuilder.nonNullable.group({
    email: [
      '',
      [Validators.email, Validators.required, Validators.availableChars],
    ],
  });
  public readonly fieldErrors = {
    email: {
      availableChars: 'common.error.chars-are-not-available',
      required: `email-confirmation.error.email-required`,
      email: `email-confirmation.error.email-invalid`,
    },
  };
  public submitErrors = new EventEmitter<void>();
  public isPending$ = new BehaviorSubject(false);
  public isFrozen$ = new BehaviorSubject(false);
  public isEmailSent = new BehaviorSubject('');
  private _user$ = this._userFacade.isUserLoaded$.pipe(
    first(Boolean),
    switchMap(() => this._userFacade.user$),
    first(),
  );

  public countdownTimer!: CountdownTimer;

  public ngOnInit(): void {
    const errorTime = this._cryptoStorageService.getItem(errorTimeKey);
    if (errorTime && isPlatformBrowser(this._platformId)) {
      this.countdownTimer = new CountdownTimer(
        new Date(+errorTime),
        defaultTimeout,
      );
      if (!this.countdownTimer.expired()) {
        this.isFrozen$.next(true);
      }
    }
    this._user$.subscribe((user) =>
      this.form.setValue({
        email: user.email,
      }),
    );
    this._useSocket();
  }

  onSubmit() {
    if (!this.form.valid) {
      this.submitErrors.next();
      return;
    }
    const { email } = this.form.getRawValue();
    this.isEmailSent.next('');
    this.isPending$.next(true);
    this._emailConfirmationApiService
      .emailConfirmRequest(this._authService.userId, { email })
      .pipe(finalize(() => this.isPending$.next(false)))
      .subscribe({
        next: () => {
          this.isEmailSent.next(email);
          const d = new Date();
          this.countdownTimer = new CountdownTimer(d, defaultTimeout);
          this._cryptoStorageService.setItem(
            errorTimeKey,
            d.getTime().toString(),
          );
          this.isFrozen$.next(true);
        },
        error: (err: HttpErrorResponse) => {
          this._formService.defaultFormError(err, this.form);
          this.submitErrors.next();
        },
      });
  }

  public promptSkip(): void {
    this._dialog.open(SkipVerificationDialogComponent, <
      MatDialogConfig<SkipData>
    >{
      panelClass: 'regular-popup-container',
      direction: this._i18nService.isRtl ? 'rtl' : 'ltr',
      data: {
        text: 'verification-modal.text.skip.route',
      },
    });
  }

  private _useSocket(): void {
    this._userSocket.userUpdated
      .pipe(first(), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this._summaryFacade.mergeStatus({
          basicStatus: BasicVerificationStatus.trial,
        });
        return this._router.navigateByUrl('/dashboard');
      });
  }
}
